<template>
  <LModal
    :visible="modelValue"
    :title="t('components.taskboard.moveIssueModal.title')"
    ok-variant="success"
    :ok-disabled="targetBoardId == null"
    size="lg"
    :submit="
      async () => {
        return { success: await save() };
      }
    "
    @update:visible="$emit('update:modelValue', $event)"
  >
    <div class="flex gap-2 flex-column">
      <InputRow
        v-model="selected"
        name="moveEventType"
        :display-name="t('components.taskboard.moveIssueModal.buttonLabel')"
      >
        <template #default="{ field: inputField }">
          <LISAInputSelectList
            v-bind="inputField"
            v-model="selected"
            horizontal
            :options="options"
            data-testid="move-event-type"
            class="w-100"
          />
        </template>
      </InputRow>
      <InputRow
        v-if="selected === SQDCBoardEventStatus.DELEGATED"
        :model-value="selectedBoardId"
        name="selectedBoardId"
        data-testid="boardSelectDropdown"
        required
        :display-name="t('components.taskboard.moveIssueModal.selectLabel')"
      >
        <template #default="{ field: inputField }">
          <SQDCBoardSelect
            v-bind="inputField"
            v-model="selectedBoardId"
            :variables="{
              siteIds: showAllSites ? undefined : board?.siteId,
            }"
            :disabled-nodes="excludedBoardsFromSearch"
            disabled-without-right="sqdc.write"
          />
        </template>
      </InputRow>
      <InputRow
        v-else
        name="escalateBoard"
        :display-name="t('components.taskboard.moveIssueModal.selectLabel')"
      >
        <SQDCBoardSelect :model-value="board?.parentId" disabled />
      </InputRow>
      <InputRow
        v-if="selected === SQDCBoardEventStatus.DELEGATED"
        v-model="showAllSites"
        name="acrossLocations"
        type="checkbox"
        :display-name="t('components.taskboard.moveIssueModal.acrossSites')"
      />
      <InputRow
        v-model="reason"
        name="delegateReason"
        type="textarea"
        :required="delegateEscalateRequired"
        :rules="[maxLength(140)]"
        :display-name="t('components.taskboard.moveIssueModal.reason')"
        test-id-override="moveEventModalDescriptionField"
      />
    </div>
  </LModal>
</template>
<script setup lang="ts">
import { maxLength } from "@/modules/formValidation";
import { computed, ref, type PropType, onMounted, inject, nextTick, watch } from "vue";
import { useTranslation } from "@/language/translation";
import {
  SQDCBoardEventStatus,
  useSQDCBoardsForSelectQuery,
  useSQDCEventMoveMutation,
} from "@/graphql";
import { BoardIdInjectionKey } from "@/views/event-action/EventActionBoardInjectionKeys";
import notEmpty from "@/functions/notEmpty";
import LModal from "@/components/LModal.vue";
import InputRow from "@/components/shared/form/InputRow.vue";
import LISAInputSelectList from "@/components/primevueWrapper/LISAInputSelectList.vue";
import { useSpecifiedBoardContext } from "@/modules/sqdc/helper/EABConfigHelper";
import type { EABEventType } from "./EventActionHelper";
import SQDCBoardSelect from "@/modules/sqdc/components/selects/SQDCBoardSelect.vue";
import { getBoardsDisabledWithoutAccess } from "@/modules/sqdc/components/selects/SQDCBoardSelectHelper";
import { useAccessRights } from "@/functions/useAccessRights";
import { hasRight } from "@/functions/authorizationState";
import store from "@/store";
import { useSQDCGlobalSettings } from "../../helper/useSQDCGlobalSettings";
const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  event: {
    type: Object as PropType<EABEventType>,
    required: true,
  },
});
const emit = defineEmits({
  "update:modelValue": (val: boolean) => val,
  saved: () => true,
});
const { t } = useTranslation();
const selected = ref<SQDCBoardEventStatus>(SQDCBoardEventStatus.ESCALATED);
const selectedBoardId = ref<number | undefined>(undefined);
const reason = ref("");

const { delegateEscalateRequired } = useSQDCGlobalSettings();

const showAllSites = ref(false);
const boardIdInjection = inject(BoardIdInjectionKey, ref<number | null>(null));
const boardId = computed(() =>
  props.event.BoardEventConnection.some((con) => con.boardId === boardIdInjection.value)
    ? (boardIdInjection.value ?? undefined)
    : props.event.originalBoardId,
);
const { board: boardUsed, boardLoading } = useSpecifiedBoardContext(computed(() => boardId.value));
const board = computed(() => boardUsed.value);
const accessRights = useAccessRights();
const { result: parentBoardResult } = useSQDCBoardsForSelectQuery(
  computed(() => ({ ids: board.value?.parentId ?? -1 })),
  computed(() => ({ enabled: board.value?.parentId != null })),
);
const parentBoardWritable = computed(() => {
  const parent = parentBoardResult.value?.SQDCBoards.find(({ id }) => id === board.value?.parentId);
  if (parent == null) return false;
  return (
    getBoardsDisabledWithoutAccess(
      [parent],
      accessRights.siteIdsWithRight("sqdc.write"),
      Number(store.getters["login/userId"]),
      hasRight("systemSettings.admin"),
    ).length === 0
  );
});
const targetBoardId = computed(() => {
  if (selected.value === SQDCBoardEventStatus.DELEGATED) return selectedBoardId.value;
  return parentBoardWritable.value ? board.value?.parentId : undefined;
});
const { mutate: moveEvent } = useSQDCEventMoveMutation(() => ({
  variables: {
    eventId: props.event.id,
    boardId: targetBoardId.value!,
    status: selected.value,
    reason: reason.value,
  },
}));
async function setDefault() {
  await nextTick();
  if (
    board.value?.parentId == null ||
    (parentBoardResult.value != null && !parentBoardWritable.value)
  ) {
    selected.value = SQDCBoardEventStatus.DELEGATED;
  } else {
    selected.value = SQDCBoardEventStatus.ESCALATED;
  }
  selectedBoardId.value = parentBoardWritable.value ? board.value?.parentId : undefined;
  reason.value = "";
}
watch(boardLoading, (val) => {
  if (!val) {
    setDefault();
  }
});
watch(parentBoardResult, (result) => {
  if (result && selected.value === SQDCBoardEventStatus.ESCALATED && !parentBoardWritable.value) {
    selected.value = SQDCBoardEventStatus.DELEGATED;
    selectedBoardId.value = undefined;
  }
});
const isRootBoard = computed(() => !board.value?.path.includes("."));
const options = computed(() => {
  return [
    {
      label: t("components.taskboard.moveIssueModal.escalate"),
      value: SQDCBoardEventStatus.ESCALATED,
      disabled: isRootBoard.value || !parentBoardWritable.value,
      disabledTooltip: isRootBoard.value
        ? t("components.taskboard.moveIssueModal.escalateButtonTooltip")
        : undefined,
    },
    {
      label: t("components.taskboard.moveIssueModal.delegate"),
      value: SQDCBoardEventStatus.DELEGATED,
    },
  ];
});
const hide = () => {
  emit("update:modelValue", false);
};
const save = async () => {
  if (targetBoardId.value == null) {
    return false;
  }
  const result = await moveEvent();
  if (result?.data?.SQDCEventMove == null) {
    return false;
  }
  emit("saved");
  hide();
  return true;
};
const excludedBoardsFromSearch = computed(() =>
  [boardId.value].filter(notEmpty).map((id) => ({ key: id, includeChildren: false })),
);
onMounted(async () => {
  setDefault();
});
</script>
